import { FC, useRef, useState } from "react";
import { useClickOutside } from "shared/model";
import { ChevronIcon } from "shared/ui/icons";
import { ConditionIcon } from "./ConditionIcon";
import type { ConditionIconName } from "./ConditionIcon";
import { IconBox } from "./IconBox";
import { useOpenUp } from "./useOpenUp";

import styles from "./ConditionTile.module.css";

export interface ConditionTileProps {
  icon: ConditionIconName;
  label: string;
  value: string;
  accent?: boolean;
  // Полный список значений: плитка раскрывает его по клику.
  list?: string[];
}

const TileBody: FC<ConditionTileProps> = ({ icon, label, value, accent }) => (
  <>
    <IconBox>
      <ConditionIcon name={icon} />
    </IconBox>
    <div className={styles.text}>
      <span className={styles.label}>{label}</span>
      <span className={`${styles.value} ${accent ? styles.accent : ""}`}>
        {value}
      </span>
    </div>
  </>
);

export const ConditionTile: FC<ConditionTileProps> = (props) => {
  const { label, list } = props;
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { openUp, measure } = useOpenUp(ref, list?.length ?? 0);

  useClickOutside(ref, () => setIsOpen(false), isOpen);

  const toggle = () => {
    if (!isOpen) measure();
    setIsOpen((prev) => !prev);
  };

  if (!list?.length) {
    return (
      <div className={styles.tile}>
        <TileBody {...props} />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={styles.dropdown}
      onKeyDown={(event) => event.key === "Escape" && setIsOpen(false)}
    >
      <button
        type="button"
        className={`${styles.tile} ${styles.clickable} ${isOpen ? styles.open : ""}`}
        aria-expanded={isOpen}
        aria-label={`${label}: show all`}
        onClick={toggle}
      >
        <TileBody {...props} />
        <ChevronIcon
          className={styles.chevron}
          direction={isOpen ? "up" : "down"}
          fill="currentColor"
          width={10}
          height={5}
        />
      </button>
      {isOpen && (
        <ul className={`${styles.list} ${openUp ? styles.up : ""}`}>
          {list.map((item) => (
            <li key={item} className={styles.item}>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
